import { useCallback, useState } from 'react';
import { Dialog } from '@mui/material';
import { IconCheck, IconX } from '@tabler/icons-react';
import * as yup from 'yup';
import { useForm, yupResolver } from '@mantine/form';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';

import { Button, IconButton, Input, SelectInput } from 'ui';

import { createUserInvite } from '../../api/user-invites';
import { getNetworkError } from '../../core/utils';
import { USER_ROLES, USER_ROLE_TO_LABEL } from '../../core/constants';
import InviteLinkBox from './InviteLinkBox';

// Mirrors the server-side rule on accept (1-50 chars, no angle brackets or control characters).
// Names are optional here: they only pre-fill the accept page, where the invitee can change them.
// eslint-disable-next-line no-control-regex
const NAME_REGEX = /^[^<>\u0000-\u001f\u007f]*$/;
const nameField = yup.string().trim().max(50, 'Must be 50 characters or fewer.').matches(NAME_REGEX, 'Invalid name.');

const formValidation = yup.object({
  email: yup.string().trim().email('Invalid email.').required('Email is required.'),
  firstName: nameField,
  lastName: nameField,
  role: yup.string().required('Role is required.'),
});

const initialValues = {
  email: '',
  firstName: '',
  lastName: '',
  role: USER_ROLES.editor,
};

const CreateUserModal = ({ open, onClose, onError, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  // The created invite; when set, the modal shows the success step with the shareable link.
  const [invite, setInvite] = useState(null);
  const form = useForm({
    initialValues,
    validate: yupResolver(formValidation),
    validateInputOnBlur: true,
  });

  const handleClose = useCallback(() => {
    if (loading) return;
    form.reset();
    setInvite(null);
    onClose();
  }, [form, loading, onClose]);

  const handleCreate = async (values) => {
    onError(false);
    setLoading(true);
    try {
      const created = await createUserInvite({
        email: values.email.trim(),
        role: values.role,
        firstName: values.firstName.trim() || undefined,
        lastName: values.lastName.trim() || undefined,
      });
      setInvite(created);
      onConfirm();
    } catch (err) {
      Sentry.captureException(err);
      onError(getNetworkError(err));
    }
    setLoading(false);
  };

  const renderSuccess = () => (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="flex size-10 items-center justify-center rounded-full bg-green-50">
          <IconCheck size={20} className="text-green-700" aria-hidden="true" />
        </div>
        <h3 className="text-h6 font-semibold">{`Invitation sent to ${invite.email}`}</h3>
        <p className="text-base text-gray-600">
          You can also share this link with them directly. It expires in 30 days.
        </p>
      </div>

      <InviteLinkBox inviteUrl={invite.inviteUrl} />

      <Button title="Done" variant="outlined" color="primary" className="w-full" onClick={handleClose} />
    </div>
  );

  const renderForm = () => (
    <>
      <p className="text-base">
        We&apos;ll email them a secure link to set their password and finish creating their account.
      </p>

      <form noValidate className="space-y-6" onSubmit={form.onSubmit(handleCreate)}>
        <Input
          id="email"
          label="Email Address"
          type="email"
          {...form.getInputProps('email')}
          error={!!form.errors.email}
          helperText={form.errors.email}
          required
          disabled={loading}
          size="xl"
        />

        <div className="flex space-x-3">
          <div className="flex-1">
            <Input
              id="firstName"
              label="First Name (optional)"
              type="text"
              {...form.getInputProps('firstName')}
              error={!!form.errors.firstName}
              helperText={form.errors.firstName}
              disabled={loading}
              size="xl"
            />
          </div>
          <div className="flex-1">
            <Input
              id="lastName"
              label="Last Name (optional)"
              type="text"
              {...form.getInputProps('lastName')}
              error={!!form.errors.lastName}
              helperText={form.errors.lastName}
              disabled={loading}
              size="xl"
            />
          </div>
        </div>

        <SelectInput
          id="role"
          label="Role"
          {...form.getInputProps('role')}
          options={[
            { value: USER_ROLES.editor, label: USER_ROLE_TO_LABEL[USER_ROLES.editor] },
            { value: USER_ROLES.administrator, label: USER_ROLE_TO_LABEL[USER_ROLES.administrator] },
          ]}
          error={!!form.errors.role}
          helperText={form.errors.role ?? 'Editors cannot make billing changes or add new users.'}
          required
          disabled={loading}
          size="lg"
        />

        <Button
          title="Send Invite"
          type="submit"
          color="primary"
          className="w-full"
          loading={loading}
          disabled={loading}
        />
      </form>
    </>
  );

  return (
    <Dialog
      maxWidth="xs"
      open={open}
      onClose={handleClose}
      sx={{ '.MuiPaper-root': { padding: 0, margin: 0, borderRadius: '12px', width: '100%' } }}
    >
      <div className="space-y-6 p-6">
        <div className="relative flex">
          {!invite && <h3 className="text-h6 font-semibold">Invite User</h3>}

          <IconButton
            Icon={<IconX size={20} className="text-gray-400" />}
            srOnly="Close"
            onClick={handleClose}
            color="transparent"
            className="absolute -right-1 -top-1 !p-1"
          />
        </div>

        {invite ? renderSuccess() : renderForm()}
      </div>
    </Dialog>
  );
};

CreateUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default CreateUserModal;
